/**
 * `noticeMessage` → the sentence the card shows (R5/R7, `specs/M6-A1` §4.4, §9).
 *
 * The keys are monday's own i18n keys, stored verbatim on import, plus the three this product
 * adds for cases monday has no key for. Anything unknown falls through to the raw key rather
 * than to an empty string: a notice nobody can read is still better than a card that silently
 * refuses to switch on.
 */
export const AUTOMATION_NOTICE_TEXT: Readonly<Record<string, string>> = {
  'store.monday.automation.error.creator_deactivated':
    'The person who created this automation is deactivated, so it was switched off.',
  'store.monday.automation.error.missing_column': 'A column this automation uses was deleted, so it was switched off.',
  'store.monday.automation.error.missing_group': 'A group this automation uses was deleted, so it was switched off.',
  'store.monday.automation.error.not_authorized_or_deleted_board':
    'This automation points at a board that was deleted or that you cannot see.',
  'store.monday.integration.error.creator_unauthorized':
    "The creator's connection to this app is no longer authorised.",
  'ez.automation.unmapped_recipe':
    'This automation came from monday and its recipe is not available here yet. It is kept, switched off, with its original configuration.',
  'ez.automation.partial_recipe':
    'Part of this automation could not be imported. It is kept, switched off, with its original configuration.',
  'ez.automation.repeated_failures': 'This automation was switched off after several runs failed in a row.',
};

export function noticeText(key: string | null): string | null {
  if (key === null || key.length === 0) return null;
  return AUTOMATION_NOTICE_TEXT[key] ?? key;
}

/** The short word next to the toggle: monday's own `state` vocabulary, humanised. */
export const AUTOMATION_STATE_TEXT: Readonly<Record<string, string>> = {
  active: 'Enabled',
  deactivated: 'Disabled',
  auto_disabled_by_user: 'Disabled',
  auto_disabled_by_deleted_resource: 'Disabled',
  auto_disabled_by_column: 'Disabled',
  error: 'Error',
  not_connected: 'Not connected',
  one_off: 'One off',
};

/** The Filter panel's Status column: **Enabled · Disabled · Error** (capture `62-card-menu`). */
export const FILTER_STATUSES = [
  { value: 'active', text: 'Enabled', color: 'var(--ez-automation-status-enabled)' },
  { value: 'deactivated', text: 'Disabled', color: 'var(--ez-automation-status-disabled)' },
  { value: 'error', text: 'Error', color: 'var(--ez-automation-status-error)' },
] as const;

/** Importance is monday's own three-value chip; every automation on this account is Minor. */
export const FILTER_IMPORTANCE = ['Minor', 'Major', 'Critical'] as const;
export const DEFAULT_IMPORTANCE = 'Minor';
